import React, { useEffect, useState } from 'react';
import { getNewsletterStats, sendNewsletter } from '../../../api/portalApi';

interface Props {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

const AdminNewsletterSection: React.FC<Props> = ({ showToast }) => {
  const [subscriberCount, setSubscriberCount] = useState<number | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ sent: number; failed: number } | null>(null);

  useEffect(() => {
    getNewsletterStats()
      .then((r) => setSubscriberCount(r.subscriberCount))
      .catch(() => showToast('Failed to load newsletter stats.', 'error'));
  // showToast is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    if (!window.confirm(`Send to ${subscriberCount ?? '?'} subscriber(s)?`)) return;
    setSending(true);
    setSent(null);
    try {
      const result = await sendNewsletter(subject.trim(), body.trim());
      setSent(result);
      setSubject('');
      setBody('');
      showToast(`Newsletter sent to ${result.sent} subscriber(s).`, 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to send newsletter.', 'error');
    } finally {
      setSending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.05)',
    color: '#f1f5f9',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{ margin: '0 0 6px', fontSize: '1.2rem', fontWeight: 800 }}>Newsletter</h2>
      <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: '0.85rem' }}>
        Send an email to all opted-in subscribers.
      </p>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderRadius: 12, background: 'rgba(200,160,106,0.12)', border: '1px solid rgba(200,160,106,0.3)', marginBottom: 28 }}>
        <span style={{ fontSize: '1.2rem' }}>📬</span>
        <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#c8a06a' }}>
          {subscriberCount === null ? '…' : subscriberCount}
        </span>
        <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>opted-in subscriber{subscriberCount !== 1 ? 's' : ''}</span>
      </div>

      <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b' }}>Subject</span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. New journeys just dropped 🧭"
            style={inputStyle}
            required
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b' }}>Body</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message here. Separate paragraphs with a blank line."
            rows={8}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6, fontFamily: 'inherit' }}
            required
          />
          <span style={{ fontSize: '0.7rem', color: '#475569' }}>Blank lines become paragraph breaks. Plain text only.</span>
        </label>

        {sent && (
          <p style={{ margin: 0, fontSize: '0.82rem', color: sent.failed > 0 ? '#fbbf24' : '#22c55e' }}>
            ✓ Sent to {sent.sent} — {sent.failed} failed
          </p>
        )}

        <button
          type="submit"
          disabled={sending || !subject.trim() || !body.trim()}
          style={{
            padding: '12px 28px',
            borderRadius: 10,
            border: 'none',
            background: sending ? 'rgba(255,255,255,0.08)' : '#c8a06a',
            color: sending ? '#94a3b8' : '#000',
            fontWeight: 800,
            fontSize: '0.88rem',
            cursor: sending ? 'wait' : 'pointer',
            alignSelf: 'flex-start',
            transition: 'background 0.2s',
          }}
        >
          {sending ? 'Sending…' : `Send to ${subscriberCount ?? '?'} subscriber${subscriberCount !== 1 ? 's' : ''}`}
        </button>
      </form>
    </div>
  );
};

export default AdminNewsletterSection;
